/**
 * Keep-alive of a page — modern implementation (React 19 `<Activity>`, Vite brands). Same API as `./index.tsx`.
 *
 * A hidden page keeps its DOM and state; React runs the cleanup of its effects when it is hidden and every
 * effect again when it is shown (effects must survive that, see `useChangeEffect` and `useRefetchOnMount`).
 */
import React, {Component, useContext, useMemo} from 'react';
import {type Location, type NavigationType, UNSAFE_LocationContext as LocationContext} from 'react-router';

import type {KeepAliveProps} from './index';

// React 19 API: `tsc` of core still uses the React 18 types (until WP22).
const {Activity} = React as unknown as {
	Activity: React.ComponentType<{mode: 'visible' | 'hidden'; children: React.ReactNode}>;
};

type LocationContextValue = {location: Location; navigationType: NavigationType};

export type {KeepAliveProps};

export const isKeepAliveSupported = true;

type ScrollKeeperProps = {visible: boolean; children: React.ReactNode};

/**
 * Window scroll of a page hidden and shown again: read before React hides it (the document gets shorter and
 * the browser clamps the scroll), restored once it is shown again, before paint.
 */
class ScrollKeeper extends Component<ScrollKeeperProps> {
	private scrollY: number | null = null;

	/**
	 * @param {ScrollKeeperProps} prevProps - Previous props
	 * @returns {number | null} - Window scroll when the page is about to be hidden
	 */
	getSnapshotBeforeUpdate(prevProps: ScrollKeeperProps): number | null {
		return prevProps.visible && !this.props.visible ? window.scrollY : null;
	}

	/**
	 * @param {ScrollKeeperProps} prevProps - Previous props
	 * @param {unknown} _prevState - Previous state
	 * @param {number | null} snapshot - `getSnapshotBeforeUpdate` result
	 */
	componentDidUpdate(prevProps: ScrollKeeperProps, _prevState: unknown, snapshot: number | null): void {
		if (snapshot !== null) {
			this.scrollY = snapshot;
		} else if (!prevProps.visible && this.props.visible && this.scrollY !== null) {
			window.scrollTo({top: this.scrollY, behavior: 'instant'});
			this.scrollY = null;
		}
	}

	/**
	 * @returns {React.ReactNode} - The page
	 */
	render() {
		return this.props.children;
	}
}

/**
 * Page kept mounted while hidden (`<Activity mode="hidden">`). With `location`, the router hooks of the page
 * (`useLocation`, `useRouterLocation`, `Link`…) return that location when it is not the router one: a page
 * being left, or hidden, keeps rendering its own URL (otherwise it would load the data of the next page).
 *
 * @param {KeepAliveProps} props - Props
 * @returns {React.ReactElement} - The page
 */
export const KeepAlive = ({visible, location, children}: KeepAliveProps) => {
	const routerContext = useContext(LocationContext) as LocationContextValue;
	const pageLocation = location as Location | undefined;
	const value = useMemo(
		() => (pageLocation && pageLocation !== routerContext.location ? {...routerContext, location: pageLocation} : routerContext),
		[routerContext, pageLocation],
	);

	return (
		<ScrollKeeper visible={visible}>
			<Activity mode={visible ? 'visible' : 'hidden'}>
				<LocationContext value={value}>{children}</LocationContext>
			</Activity>
		</ScrollKeeper>
	);
};
